import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, ArrowRight, FileText, Megaphone, MessageCircleQuestion, Bell } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { EmptyState } from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";
import { formatCurrency, formatMonths, formatDate } from "@/lib/format";
import { DirectoryOptIn } from "@/components/investor/DirectoryOptIn";
import { SwipeDeck } from "@/components/investor/SwipeDeck";

function FollowingCard({ items }) {
  if (!items.length) return null;
  return (
    <Card className="mb-6 rounded-xl border border-brand/25 bg-brand/[0.05] p-5" data-testid="portfolio-following">
      <div className="flex items-center gap-2">
        <Bell className="h-4 w-4 text-brand-ink" />
        <h2 className="h-display text-base font-semibold text-foreground">Founders keeping you posted</h2>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">You tapped "keep me posted" on these decks. Once the founder adds you as an investor, the company appears below with live metrics and updates.</p>
      <ul className="mt-3 space-y-2">
        {items.map((f) => (
          <li key={f.company_id} className="flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm" data-testid={`following-${f.company_id}`}>
            <span className="font-semibold text-foreground">{f.company_name}</span>
            <span className="text-xs text-muted-foreground">{f.deck_name} · {formatDate(f.responded_at)}</span>
            <Badge className="ml-auto border-amber-200 bg-amber-50 font-normal text-amber-800">Waiting on founder</Badge>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export default function Portfolio() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [summary, setSummary] = useState(null);
  const [feed, setFeed] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [c, s, f, fo] = await Promise.all([
          api.get(`/portal/companies`),
          api.get(`/portal/portfolio-summary`).catch(() => ({ data: null })),
          api.get(`/portal/feed`).catch(() => ({ data: [] })),
          api.get(`/portal/following`).catch(() => ({ data: [] })),
        ]);
        setCompanies(c.data);
        setSummary(s.data);
        setFeed(f.data);
        setFollowing(fo.data || []);
      }
      catch (e) { console.error("Failed to load portfolio", e); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = summary?.totals;
  const byCompany = Object.fromEntries((summary?.companies || []).map((r) => [r.company_id, r]));

  return (
    <AppShell title="Portfolio" subtitle="Companies you're onboarded to">
      {!loading && totals?.invested > 0 && (
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="portfolio-summary">
          <Card className="rounded-xl border bg-card p-4">
            <div className="text-[11px] text-muted-foreground">Total invested</div>
            <div className="h-display text-2xl font-semibold tabular-nums" data-testid="portfolio-invested">{formatCurrency(totals.invested)}</div>
          </Card>
          <Card className="rounded-xl border bg-card p-4">
            <div className="text-[11px] text-muted-foreground">Implied value</div>
            <div className="h-display text-2xl font-semibold tabular-nums" data-testid="portfolio-value">{totals.implied_value ? formatCurrency(totals.implied_value) : "-"}</div>
          </Card>
          <Card className="rounded-xl border bg-card p-4">
            <div className="text-[11px] text-muted-foreground">MOIC</div>
            <div className={`h-display text-2xl font-semibold tabular-nums ${totals.moic >= 1 ? "text-emerald-600" : "text-amber-600"}`} data-testid="portfolio-moic">{totals.moic ? `${totals.moic}x` : "-"}</div>
          </Card>
          <Card className="rounded-xl border bg-card p-4">
            <div className="text-[11px] text-muted-foreground">Holdings</div>
            <div className="h-display text-2xl font-semibold tabular-nums" data-testid="portfolio-count">{totals.companies_with_holdings}</div>
          </Card>
        </div>
      )}
      {!loading && <FollowingCard items={following} />}
      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" data-testid="loading-skeleton" />)}
        </div>
      ) : companies.length === 0 ? (
        <EmptyState icon={Briefcase} title="No companies yet" description="When a founder adds you as an investor (using your account email), their company will appear here." />
      ) : (
        <SwipeDeck count={companies.length} testid="portfolio-deck">
          {companies.map((c) => {
            const lm = c.latest_metrics;
            const inv = byCompany[c.id];
            return (
              <Card key={c.id} onClick={() => navigate(`/portal/company/${c.id}`)} className="group cursor-pointer rounded-xl border bg-card p-5 transition-shadow hover:shadow-[var(--shadow-md)]" data-testid={`portfolio-company-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <span className="h-display text-lg font-semibold">{c.name?.charAt(0)?.toUpperCase()}</span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                </div>
                <div className="mt-3 h-display text-base font-semibold text-foreground">{c.name}</div>
                <div className="mt-0.5 flex flex-wrap gap-1.5">
                  {c.sector && <Badge variant="secondary" className="font-normal">{c.sector}</Badge>}
                  {c.stage && <Badge variant="secondary" className="font-normal">{c.stage}</Badge>}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 border-t pt-3">
                  <div><div className="text-[11px] text-muted-foreground">MRR</div><div className="h-display text-sm font-semibold tabular-nums">{lm ? formatCurrency(lm.mrr) : "-"}</div></div>
                  <div><div className="text-[11px] text-muted-foreground">Runway</div><div className="h-display text-sm font-semibold tabular-nums">{lm ? formatMonths(lm.runway_months) : "-"}</div></div>
                </div>
                {inv?.invested > 0 && (
                  <div className="mt-3 grid grid-cols-2 gap-3 border-t pt-3" data-testid={`company-holding-${c.id}`}>
                    <div><div className="text-[11px] text-muted-foreground">Your investment</div><div className="h-display text-sm font-semibold tabular-nums">{formatCurrency(inv.invested)}</div></div>
                    <div><div className="text-[11px] text-muted-foreground">MOIC</div><div className={`h-display text-sm font-semibold tabular-nums ${inv.moic >= 1 ? "text-emerald-600" : ""}`}>{inv.moic ? `${inv.moic}x` : "-"}</div></div>
                  </div>
                )}
                <div className="mt-3 flex items-center gap-1 text-xs text-muted-foreground"><FileText className="h-3.5 w-3.5" />{c.updates_count} update{c.updates_count === 1 ? "" : "s"}</div>
              </Card>
            );
          })}
        </SwipeDeck>
      )}
      {!loading && feed.length > 0 && (
        <div className="mt-8" data-testid="portfolio-feed">
          <h2 className="mb-3 h-display text-lg font-semibold text-foreground">Latest across your portfolio</h2>
          <div className="space-y-2">
            {feed.slice(0, 12).map((e, i) => (
              <button key={`${e.type}-${i}`} onClick={() => navigate(e.link)}
                className="flex w-full items-center gap-3 rounded-xl border bg-card p-3 text-left transition-colors hover:border-primary/40"
                data-testid={`feed-item-${i}`}>
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                  e.type === "announcement" ? "bg-amber-50 text-amber-600" : e.type === "answer" ? "bg-emerald-50 text-emerald-600" : "bg-primary/10 text-primary"}`}>
                  {e.type === "announcement" ? <Megaphone className="h-4 w-4" /> : e.type === "answer" ? <MessageCircleQuestion className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground">{e.title}</span>
                  <span className="block truncate text-xs text-muted-foreground">{e.company_name}{e.detail ? ` · ${e.detail}` : ""}</span>
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">{formatDate(e.at)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <DirectoryOptIn />
    </AppShell>
  );
}

